import {Box, getValueByDevice, HStack, Stack, useDevice, VStack} from 'platform/foundation';

import {range} from 'ramda';

import {SkeletonLoadingBox} from '@dms/shared';

import {
  SALES_LEAGUE_PODIUM_CARD_PADDING_HORIZONTAL,
  SALES_LEAGUE_PODIUM_IDENTITY_SPACING,
  SALES_LEAGUE_PODIUM_NAME_SPACING,
  SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE,
  SALES_LEAGUE_PODIUM_PLACES,
  SALES_LEAGUE_PODIUM_RUNNER_UP_FLEX,
  SALES_LEAGUE_PODIUM_RUNNER_UP_NAME_LINE,
  SALES_LEAGUE_PODIUM_RUNNER_UP_PADDING_VERTICAL,
  SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE,
  SALES_LEAGUE_PODIUM_SECTION_SPACING,
  SALES_LEAGUE_PODIUM_STAT_COUNT,
  SALES_LEAGUE_PODIUM_STAT_LABEL_LINE,
  SALES_LEAGUE_PODIUM_STAT_SPACING,
  SALES_LEAGUE_PODIUM_SUBTITLE_LINE,
  SALES_LEAGUE_PODIUM_WINNER_FLEX,
  SALES_LEAGUE_PODIUM_WINNER_NAME_LINE,
  SALES_LEAGUE_PODIUM_WINNER_PADDING_VERTICAL,
  SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX,
  SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE,
} from '../constants/SALES_LEAGUE_PODIUM';

const CARD_BACKGROUND_COLOR = 'palettes.neutral.20.100' as const;
const PODIUM_SPACING = 4;
const STAT_RULE_BORDER = '1px solid';
const STAT_RULE_COLOR = 'palettes.neutral.30.100' as const;

const MEDAL_LABEL_WIDTH = 20;
const NAME_WIDTH = 32;
const SUBTITLE_WIDTH = 18;
const STAT_LABEL_WIDTH = 14;
const STAT_VALUE_WIDTH = 20;

/**
 * Stands in for the podium while the board loads. It mirrors the real card's geometry
 * constant for constant, and every skeleton line is as tall as the text it replaces, so the
 * two are the same height and nothing shifts when the data lands.
 *
 * Deliberately neutral rather than gold, silver and bronze: which rep or branch takes which
 * medal is exactly what is not known yet.
 */
export function SalesLeaguePodiumSkeleton() {
  const device = useDevice();
  const isPodiumRow = getValueByDevice(device, false, false, true);

  return (
    <Stack
      direction={isPodiumRow ? 'row' : 'column'}
      align={isPodiumRow ? 'center' : 'stretch'}
      spacing={PODIUM_SPACING}
    >
      {range(0, SALES_LEAGUE_PODIUM_PLACES).map((place) => {
        const isWinner = place === SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX;
        const visualSize = isWinner
          ? SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE
          : SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE;
        const nameLine = isWinner
          ? SALES_LEAGUE_PODIUM_WINNER_NAME_LINE
          : SALES_LEAGUE_PODIUM_RUNNER_UP_NAME_LINE;

        return (
          <Box
            key={place}
            flex={
              isPodiumRow // eslint-disable-line no-nested-ternary -- Pre-existing violation
                ? isWinner
                  ? SALES_LEAGUE_PODIUM_WINNER_FLEX
                  : SALES_LEAGUE_PODIUM_RUNNER_UP_FLEX
                : undefined
            }
            backgroundColor={CARD_BACKGROUND_COLOR}
            borderRadius="medium"
            paddingHorizontal={SALES_LEAGUE_PODIUM_CARD_PADDING_HORIZONTAL}
            paddingVertical={
              isWinner
                ? SALES_LEAGUE_PODIUM_WINNER_PADDING_VERTICAL
                : SALES_LEAGUE_PODIUM_RUNNER_UP_PADDING_VERTICAL
            }
          >
            <VStack spacing={SALES_LEAGUE_PODIUM_SECTION_SPACING}>
              <SkeletonLoadingBox
                isLoading
                width={MEDAL_LABEL_WIDTH}
                height={SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE}
              />
              <HStack spacing={SALES_LEAGUE_PODIUM_IDENTITY_SPACING} align="center">
                <SkeletonLoadingBox
                  isLoading
                  width={visualSize}
                  height={visualSize}
                  borderRadius="circular"
                />
                <VStack spacing={SALES_LEAGUE_PODIUM_NAME_SPACING}>
                  <SkeletonLoadingBox isLoading width={NAME_WIDTH} height={nameLine} />
                  <SkeletonLoadingBox
                    isLoading
                    width={SUBTITLE_WIDTH}
                    height={SALES_LEAGUE_PODIUM_SUBTITLE_LINE}
                  />
                </VStack>
              </HStack>
              {/* The real card rules the stats off with a top border; matching it here keeps
                  the two exactly the same height. */}
              <Box
                paddingTop={SALES_LEAGUE_PODIUM_SECTION_SPACING}
                borderTop={STAT_RULE_BORDER}
                borderColor={STAT_RULE_COLOR}
              >
                <HStack spacing={SALES_LEAGUE_PODIUM_STAT_SPACING}>
                  {range(0, SALES_LEAGUE_PODIUM_STAT_COUNT).map((stat) => (
                    <VStack key={stat} spacing={SALES_LEAGUE_PODIUM_NAME_SPACING}>
                      <SkeletonLoadingBox
                        isLoading
                        width={STAT_LABEL_WIDTH}
                        height={SALES_LEAGUE_PODIUM_STAT_LABEL_LINE}
                      />
                      <SkeletonLoadingBox isLoading width={STAT_VALUE_WIDTH} height={nameLine} />
                    </VStack>
                  ))}
                </HStack>
              </Box>
            </VStack>
          </Box>
        );
      })}
    </Stack>
  );
}
